import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent, FilterGroup } from '@custom-components/general/table-filter-bar';
import { partnerFilterGroup } from '@core/utils/partner-filter-group.util';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import {
  APP_PATHS,
  PAGINATION_DEFAULT,
  SERVICES_TABLE_COLUMNS_CONFIG,
  XEMEX_OID,
} from '@core/constants';
import { Service, ServiceFiltersModel, ServiceStatus, getUserActivePartner } from '@core/models';
import { CompanyService, LocationService } from '@core/services';
import { ServiceStore } from '@root/app/store/service.store';
import { UserStore } from '@root/app/store/user.store';
import {
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';

@Component({
  selector: 'xemex-services-page',
  imports: [
    TableComponent,
    TableFilterBarComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './services-page.component.html',
})
export class ServicesPageComponent {
  readonly serviceStore = inject(ServiceStore);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly locationService = inject(LocationService);
  private readonly companyService = inject(CompanyService);
  private readonly userStore = inject(UserStore);

  /** The partner pill is only offered to Xemex users, who can see every partner's data. */
  protected readonly isXemexUser = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  protected readonly columns = SERVICES_TABLE_COLUMNS_CONFIG;
  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.services') },
  ];

  private filter: ServiceFiltersModel = {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  };
  // When the page is opened scoped to a partner (via the partnerOid query param),
  // services are fetched through the per-partner OID endpoint.
  private partnerOid?: string;

  private readonly packageNameMap = toSignal(
    toObservable(this.serviceStore.services).pipe(
      switchMap(resp => {
        const services = resp.services;
        if (!services.length) return of({} as Record<number, string>);
        return combineLatest(
          services.map(s =>
            (s.package ?? of(undefined)).pipe(
              map(p => ({ id: s.serviceId, name: p?.packageDescription ?? p?.packageCode ?? '' })),
            ),
          ),
        ).pipe(map(entries => Object.fromEntries(entries.map(e => [e.id, e.name]))));
      }),
    ),
    { initialValue: {} as Record<number, string> },
  );

  private readonly locationDisplayMap = toSignal(
    toObservable(this.serviceStore.services).pipe(
      switchMap(resp => {
        const services = resp.services;
        if (!services.length) return of({} as Record<number, string>);
        return combineLatest(
          services.map(s =>
            this.locationService.getLocationDetail(String(s.locationId)).pipe(
              catchError(() => of(undefined)),
              startWith(undefined),
              map(loc => {
                const streetPart = [
                  loc?.locationStreet,
                  loc?.locationStreetNumber,
                  loc?.locationStreetNumberAddition,
                ]
                  .filter(v => v != null && v !== '')
                  .join(' ');
                const display = [streetPart, loc?.locationCity].filter(Boolean).join(', ');
                return { id: s.serviceId, display };
              }),
            ),
          ),
        ).pipe(map(entries => Object.fromEntries(entries.map(e => [e.id, e.display]))));
      }),
    ),
    { initialValue: {} as Record<number, string> },
  );

  protected readonly tableData = computed(() => {
    const nameMap = this.packageNameMap() ?? {};
    const locMap = this.locationDisplayMap() ?? {};
    return this.serviceStore.services().services.map(s => ({
      ...s,
      formattedPrice: `€${(s.priceInCents / 100).toFixed(2)}`,
      packageName: nameMap[s.serviceId] ?? '',
      locationDisplay: locMap[s.serviceId] ?? '',
    })) as unknown as Record<string, unknown>[];
  });

  private readonly activeFilters = signal<Record<string, string[]>>({});

  protected readonly filteredTableData = computed(() => {
    const data = this.tableData();
    const filters = this.activeFilters();

    return data.filter(row => {
      const pkgs = filters['package'];
      if (pkgs?.length && !pkgs.includes(String(row['packageName']))) return false;
      const statuses = filters['status'];
      if (statuses?.length && !statuses.includes(String(row['status']))) return false;
      const createdOns = filters['createdOn'];
      if (createdOns?.length) {
        if (!createdOns.includes(this.toMonthYear(String(row['createdOn'] ?? '')))) return false;
      }
      const activatedOns = filters['activatedOn'];
      if (activatedOns?.length) {
        if (!activatedOns.includes(this.toMonthYear(String(row['activatedOn'] ?? ''))))
          return false;
      }
      return true;
    });
  });

  protected readonly totalCount = computed(() => this.serviceStore.services().numberOfItems);

  protected readonly filterGroups = computed<FilterGroup[]>(() => {
    const data = this.tableData();
    const packages = [...new Set(data.map(r => String(r['packageName'])).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );
    const statuses = Object.values(ServiceStatus);
    const createdOns = [
      ...new Set(data.map(r => this.toMonthYear(String(r['createdOn'] ?? ''))).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));
    const activatedOns = [
      ...new Set(data.map(r => this.toMonthYear(String(r['activatedOn'] ?? ''))).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));

    return [
      {
        key: 'package',
        label: this.translate.instant('services.columns.package'),
        options: packages,
      },
      {
        key: 'status',
        label: this.translate.instant('services.columns.status'),
        options: statuses,
      },
      {
        key: 'createdOn',
        label: this.translate.instant('services.columns.createdOn'),
        options: createdOns,
      },
      {
        key: 'activatedOn',
        label: this.translate.instant('services.columns.activatedOn'),
        options: activatedOns,
      },
      ...(this.isXemexUser()
        ? [
            partnerFilterGroup(
              this.companyService,
              this.translate.instant('services.filter.partner'),
              this.translate.instant('general.placeholderInput'),
            ),
          ]
        : []),
    ];
  });

  /** Deep-linked, e.g. from a partner's detail page — pre-selects the "partner" pill (the
   * filter bar only reads this once, on init). */
  protected readonly initialFilters = computed<Record<string, string[]>>(() => {
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    const filters: Record<string, string[]> = {};
    if (partnerOid && this.isXemexUser()) filters['partner'] = [partnerOid];
    return filters;
  });

  private readonly filterInputs$ = new Subject<ServiceFiltersModel>();

  constructor() {
    this.getServices();

    this.filterInputs$
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500), distinctUntilChanged())
      .subscribe(payload => this.serviceStore.getServicesFilterMethod(payload));
  }

  private getServices(): void {
    this.partnerOid = this.route.snapshot.queryParamMap.get('partnerOid') ?? undefined;
    this.serviceStore.getServicesFilterMethod({ ...this.filter, partnerOid: this.partnerOid });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.activeFilters.set(filters);
    const partnerOid = filters['partner']?.[0];
    if (partnerOid === this.partnerOid) return;
    this.partnerOid = partnerOid;
    this.serviceStore.getServicesFilterMethod({ ...this.filter, partnerOid: this.partnerOid });
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { partnerOid: partnerOid ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onPageChange(evt: PageEvent): void {
    this.filter = { ...this.filter, offset: evt.pageIndex };
    this.serviceStore.getServicesFilterMethod({ ...this.filter, partnerOid: this.partnerOid });
  }

  protected onRowClick(row: Record<string, unknown>): void {
    const service = row as unknown as Service;
    this.serviceStore.setSelectedService(service);
    this.router.navigate(['/', APP_PATHS.Services, service.serviceId]);
  }

  protected onRefreshClicked(): void {
    this.serviceStore.getServicesFilterMethod({ ...this.filter, partnerOid: this.partnerOid });
  }

  private toMonthYear(dateStr: string): string {
    if (!dateStr || dateStr === 'undefined' || dateStr === 'null') return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }
}
